import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CleanedData } from "../../types/global.d";
import { getAuthToken } from "../../api/auth";
import {
  addCleanedImagesDirect,
  editCleaned as apiEditCleaned,
  editCleanedImagesDirect,
  submitNotCleaned as apiSubmitNotCleaned,
} from "../../api/reports";
import { uploadImagesDirect, getUploadErrorMessage } from "../../api/uploads";
import { getErrorMessage } from "../../utils/api";
import { useDataContext } from "../../contexts/DataContext";
import { useUserContext } from "../../contexts/UserContext";
import { UserStatus, STATIC_URL } from "../../constants";
import ImageEditor from "./ImageEditor";
import "../styles/EditCleanupDialog.css";

// Helper function to get full image URL
const getImageUrl = (imagePath: string): string => {
  if (!imagePath) return "";

  // If it's already a full URL (blob or http), return as is
  if (
    imagePath.startsWith("blob:") ||
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://")
  ) {
    return imagePath;
  }

  // If it's a relative path, construct the full URL using Google Cloud Storage
  return `${STATIC_URL}${imagePath.replace(/^\//, "")}`;
};

interface EditCleanupDialogProps {
  cleanedData: CleanedData;
  reportId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const getCleanerTypes = (t: any) => [
  { value: 0, label: t("cleanup.individual") },
  { value: 1, label: t("cleanup.group") },
  { value: 2, label: t("cleanup.administration") },
];

const EditCleanupDialog: React.FC<EditCleanupDialogProps> = ({
  cleanedData,
  reportId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { updateData, loadReportDetails } = useDataContext();
  const { user } = useUserContext();
  const [loading, setLoading] = useState(false);
  const [markingNotCleaned, setMarkingNotCleaned] = useState(false);

  const isAdmin = user?.status === UserStatus.admin;
  const isOwner = user?.nickname === cleanedData.user?.nickname;
  const canEditImages = isAdmin || (isOwner && !cleanedData.verified);

  // Form state
  const [comment, setComment] = useState(cleanedData.comment || "");
  const [amount, setAmount] = useState<number>(cleanedData.amount || 1);
  const [cleaner, setCleaner] = useState<number>(
    cleanedData.cleaner !== undefined
      ? cleanedData.cleaner
      : cleanedData.amount && cleanedData.amount > 0
      ? 1
      : 0
  );

  // Image editing state
  const [images, setImages] = useState<string[]>(
    cleanedData.images?.map(getImageUrl) || []
  );
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);

  const handleImagesChange = useCallback(
    (newImages: string[], newFiles: File[]) => {
      setImages(newImages);
      setNewImageFiles(newFiles);
    },
    []
  );

  const isDataChanged = useCallback(() => {
    const originalData = {
      comment: cleanedData.comment || "",
      amount: cleanedData.amount || 1,
      cleaner:
        cleanedData.cleaner !== undefined
          ? cleanedData.cleaner
          : cleanedData.amount && cleanedData.amount > 0
          ? 1
          : 0,
    };
    const currentData = {
      comment,
      amount,
      cleaner,
    };

    const originalImageUrls = cleanedData.images?.map(getImageUrl) || [];
    const imagesChanged =
      JSON.stringify(images) !== JSON.stringify(originalImageUrls);

    return (
      JSON.stringify(originalData) !== JSON.stringify(currentData) ||
      imagesChanged
    );
  }, [comment, amount, cleaner, images, cleanedData]);

  const handleMarkNotCleaned = async () => {
    if (markingNotCleaned || loading) return;
    if (
      !window.confirm(
        "Are you sure this location is not cleaned? This will remove the cleanup data."
      )
    )
      return;

    const token = getAuthToken();
    if (!token) {
      alert(t("auth.authRequired"));
      return;
    }

    setMarkingNotCleaned(true);
    try {
      await apiSubmitNotCleaned(reportId, token);
      await updateData();
      await loadReportDetails(reportId, true);
      alert(t("cleanup.markedNotCleaned"));
      onSuccess();
    } catch (error) {
      alert(`${t("errors.failedToMarkNotCleaned")}: ${getErrorMessage(error)}`);
    } finally {
      setMarkingNotCleaned(false);
    }
  };

  const handleSubmit = async () => {
    if (loading || !isDataChanged()) return;

    const token = getAuthToken();
    if (!token) {
      alert(t("auth.authRequired"));
      return;
    }

    if (images.length === 0) {
      alert(t("report.atLeastOneImage"));
      return;
    }

    setLoading(true);
    try {
      // Check if data changed (non-image fields)
      const originalData = {
        comment: cleanedData.comment || "",
        amount: cleanedData.amount || 1,
        cleaner:
          cleanedData.cleaner !== undefined
            ? cleanedData.cleaner
            : cleanedData.amount && cleanedData.amount > 0
            ? 1
            : 0,
      };
      const currentData = {
        comment,
        amount,
        cleaner,
      };
      const dataChanged =
        JSON.stringify(originalData) !== JSON.stringify(currentData);

      // Update cleanup data if changed
      if (dataChanged) {
        await apiEditCleaned(
          {
            _id: cleanedData._id,
            comment,
            amount: cleaner !== 2 ? amount : undefined,
            cleaner,
          },
          token
        );
      }

      // Check if images changed
      const originalImageUrls = cleanedData.images?.map(getImageUrl) || [];
      const imagesChanged =
        JSON.stringify(images) !== JSON.stringify(originalImageUrls);

      if (imagesChanged) {
        const currentExistingImages = images.filter(
          (image) => !image.startsWith("blob:")
        );
        const existingImagesUnchanged =
          JSON.stringify(currentExistingImages) ===
          JSON.stringify(originalImageUrls);

        if (newImageFiles.length > 0) {
          // New images go straight to GCS; submit only their keys.
          const imageKeys = await uploadImagesDirect(newImageFiles, token);
          if (existingImagesUnchanged) {
            await addCleanedImagesDirect(
              { _id: cleanedData._id, imageKeys },
              token,
            );
          } else {
            await editCleanedImagesDirect(
              { _id: cleanedData._id, imageKeys },
              token,
            );
          }
        } else if (!existingImagesUnchanged) {
          throw new Error(t("errors.failedToUploadImages"));
        }
      }

      await updateData();
      await loadReportDetails(reportId, true);
      alert(t("cleanup.cleanupUpdated"));
      onSuccess();
    } catch (error) {
      alert(
        `${t("errors.failedToUpdateCleanup")}: ${getUploadErrorMessage(error, t)}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const CLEANER_TYPES = getCleanerTypes(t);

  return (
    <>
      <div className="edit-dialog-overlay" onClick={onClose} />
      <div className="edit-dialog edit-cleanup-dialog">
        <div className="edit-dialog-header">
          <h2>{t("cleanup.editCleanup")}</h2>
          <button className="edit-dialog-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="edit-dialog-content">
          {/* Images Section */}
          {canEditImages ? (
            <div className="edit-section">
              <label className="edit-label">{t("report.images")}</label>
              <ImageEditor
                images={images}
                onImagesChange={handleImagesChange}
                disabled={loading || markingNotCleaned}
                minImages={1}
              />
            </div>
          ) : (
            <div className="edit-section">
              <label className="edit-label">{t("report.imagesReadonly")}</label>
              <div className="images-readonly">
                {images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`Cleanup ${index + 1}`}
                    className="readonly-image"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Comment Section */}
          <div className="edit-section">
            <label className="edit-label">{t("report.comment")}</label>
            <textarea
              className="edit-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("report.addCommentPlaceholder")}
              rows={3}
            />
          </div>

          {/* Cleaner Type Section */}
          <div className="edit-section">
            <label className="edit-label">{t("cleanup.cleanedBy")}</label>
            <div className="options-grid">
              {CLEANER_TYPES.map((type) => (
                <button
                  key={type.value}
                  className={`option-btn ${
                    cleaner === type.value ? "active" : ""
                  }`}
                  onClick={() => setCleaner(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cleanup Amount Section - Only show if not Administration */}
          {cleaner !== 2 && (
            <div className="edit-section">
              <label className="edit-label">
                {t("cleanup.cleanupAmount")}
                <div className="amount-display">
                  <span className="amount-value">{amount}</span>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/trash${
                      amount < 10 ? 0 : amount < 20 ? 1 : 2
                    }.png`}
                    alt={`Amount ${amount}`}
                    className="pollution-level-icon"
                  />
                </div>
              </label>
              <input
                type="range"
                min="1"
                max="40"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="pollution-slider"
              />
            </div>
          )}
        </div>

        <div className="edit-dialog-footer">
          <div className="footer-left">
            <button
              className="edit-delete-btn"
              onClick={handleMarkNotCleaned}
              disabled={loading || markingNotCleaned}
            >
              {markingNotCleaned ? t("cleanup.marking") : t("admin.notCleaned")}
            </button>
          </div>
          <div className="footer-right">
            <button className="edit-cancel-btn" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              className="edit-submit-btn"
              onClick={handleSubmit}
              disabled={loading || markingNotCleaned || !isDataChanged()}
            >
              {loading ? t("report.saving") : t("report.saveChanges")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditCleanupDialog;
